import { useMemo } from 'react';
import { Icon as BlueIcon } from '@blueprintjs/core';
import { createState, useHookstate } from '@hookstate/core';

import {
  IdentificationRequestStatus,
  IIdentificationRequest,
} from '@models/IdentificationRequests.model';
import { useMutation, useQueryClient } from 'react-query';
import { changeChatStatus } from '@services/queries/messages';
import {
  StatusContainer,
  StatusWrapper,
  StatusSelect,
  Status,
  StatusColor,
  StatusName,
  RequestDetailsLink,
  Menu,
  MenuItem,
} from './ChatStatusDropDown.styles';
import { RequestDetails } from '../RequestDetails/RequestDetails';
import { requestDetailsState } from '../RequestDetails/RequestDetails.raw';

export const statusMenuDisplayState = createState<boolean>(false);

interface IChatStatusDropDown {
  identificationRequest: IIdentificationRequest;
}

const ChatStatusDropDown = ({ identificationRequest }: IChatStatusDropDown) => {
  const openStatusMenu = useHookstate(statusMenuDisplayState);

  const queryClient = useQueryClient();

  const opeDetailsState = useHookstate(requestDetailsState);

  const selectedRequestValue = identificationRequest;

  const statusRead = useMemo(() => {
    if (selectedRequestValue?.status === 'closed') return 'Closed';
    if (selectedRequestValue?.status === 'confirm_product')
      return 'Confirm Product';
    if (selectedRequestValue?.status === 'information_needed')
      return 'Information Needed';
    if (selectedRequestValue?.status === 'resolved') return 'Resolved';
    return 'Open';
  }, [selectedRequestValue]);

  const statusMutation = useMutation(changeChatStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(['identificationRequests']);
    },
  });

  const handleSelectedStatus = (status: IdentificationRequestStatus) => {
    if (selectedRequestValue) {
      statusMutation.mutate({
        status,
        threadId: selectedRequestValue.threadId,
      });
    }
    openStatusMenu.set(false);
  };

  return (
    <StatusContainer onClick={e => e.stopPropagation()}>
      <RequestDetails />

      <StatusWrapper>
        <StatusSelect onClick={() => openStatusMenu.set(!openStatusMenu.get())}>
          <Status>
            <StatusColor data-status={selectedRequestValue?.status} />
            <StatusName>{statusRead}</StatusName>
          </Status>
          <BlueIcon icon="chevron-down" color="#8F95B2" />
        </StatusSelect>
        {openStatusMenu.get() && (
          <Menu>
            <MenuItem onClick={() => handleSelectedStatus('open')}>
              Open
            </MenuItem>
            <MenuItem
              onClick={() => handleSelectedStatus('information_needed')}
            >
              Information Needed
            </MenuItem>
            <MenuItem onClick={() => handleSelectedStatus('confirm_product')}>
              Confirm Product
            </MenuItem>
            <hr />
            <MenuItem onClick={() => handleSelectedStatus('resolved')}>
              Resolved
            </MenuItem>
            <MenuItem onClick={() => handleSelectedStatus('closed')}>
              Closed
            </MenuItem>
          </Menu>
        )}
      </StatusWrapper>

      <RequestDetailsLink
        onClick={() => {
          opeDetailsState.set(true);
          openStatusMenu.set(false);
        }}
      >
        See Request Details
      </RequestDetailsLink>
    </StatusContainer>
  );
};

export { ChatStatusDropDown };
